/**
 * Metoder for konvertering mellom Date- og temporal-API et
 * Se https://tc39.es/proposal-temporal for dokumentasjon av temporal APIet
 */

import {Temporal} from "temporal-polyfill";
import {isValid} from "date-fns";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import PlainDate = Temporal.PlainDate;
import PlainTime = Temporal.PlainTime;
import ZonedDateTime = Temporal.ZonedDateTime;
import Instant = Temporal.Instant;
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";

/** Konverterer {@link Date} til {@link PlainDate}
 * Dersom angitt dato er null eller 'invalid' returneres null ('invalid' er et Date-objekt som er ugyldig) */
export function dateSomPlainDate(dato: Date): PlainDate;
export function dateSomPlainDate(dato: Date | null): PlainDate | null {
    if (erNil(dato) || !isValid(dato)) return null;
    return new PlainDate(dato.getFullYear(), dato.getMonth() + 1, dato.getDate());
}

/** Konverterer {@link PlainDate} til {@link Date}.
 * MERK at returnert date-instans vil være i lokal tidssone (hva enn dette er i den aktuelle nettleser) og
 * KLOKKESLETT vil være satt til 00:00 i denne tidssonen. */
export function plainDateSomDate(dato: PlainDate): Date;
export function plainDateSomDate(dato: PlainDate | null): Date | null {
    if (erNil(dato)) return null;
    return new Date(dato.year, dato.month - 1, dato.day);
}

/** Konverterer {@link PlainDate} til streng.
 * BRUK metoder i {@link TemporalFormatering} for brukerrettede representasjoner av temporal instanser!
 * Returnerer på yyyy-mm-dd */
export function plainDateSomStreng(dato: PlainDate | null): string | null {
    if (erNil(dato)) return null;
    return dato.toString();
}

/** Konverterer streng til {@link PlainTime} */
export function strengSomPlainTime(klokkeslett: string | null): PlainTime | null {
    if (erNil(klokkeslett)) return null;
    return PlainTime.from(klokkeslett);
}

/** Konverterer {@link PlainTime} til streng for bruk ifbm serialisering.
 * BRUK metoder i {@link TemporalFormatering} for brukerrettede representasjoner av temporal instanser!
 *
 * Merk at vi returnerer hh:mm, i motsetning til PlainTime.toString() som returnerer hh:mm:ss.
 * Inntil Temporal er rullet fullt ut forventer en ajs-kode at tidspunkt er representert på formen hh:mm */
export function plainTimeSomStreng(klokkeslett: PlainTime | null): string | null {
    if (erNil(klokkeslett)) return null;
    return klokkeslett.toString({smallestUnit: 'minutes', roundingMode: 'trunc'});
}

export function zonedDateTimeSomDate(dato: ZonedDateTime | null): Date | null {
    if (erNil(dato)) return null;
    return new Date(dato.epochMilliseconds);
}

export function dateSomZonedDateTime(dato: Date): ZonedDateTime;
export function dateSomZonedDateTime(dato: Date, tidssone:Tidssoneinformasjon): ZonedDateTime;
export function dateSomZonedDateTime(dato: Date | null, tidssone:Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTime | null {
    if (erNil(dato)) return null;
    return Instant.fromEpochMilliseconds(dato.getTime()).toZonedDateTimeISO(tidssone.somZoneId());
}
